import type {
  CycleOutcome,
  CycleOutcomePatch,
} from "@src/packages/cycles/hooks/useCycles";

/**
 * The body an outcome write needs, and why it is so small.
 *
 * This codebase repeats a rule that is not what the API does: "a cycle PATCH
 * replaces the whole `outcome` sub-object, so anything omitted is destroyed."
 * The server routes the outcome on the presence of an `id` — `updateOutcome`
 * when the body carries one, `createOutcome` when it does not (dashboard-api
 * `src/api/cycles/cycle.service.ts`) — and `updateOutcome` is
 * `db.update(outcomes).set(record)`, a partial column update. Columns the body
 * omits are left exactly as they were. `acknowledgedAt` is not even on the PATCH
 * surface, and it survives an update for precisely that reason.
 *
 * What actually destroyed data was the *missing* id. `outcomes` has no unique
 * index on `cycle_id`, so a write without one INSERTs a second outcome row for
 * the same cycle, and reads order by `recorded_at` — so the new row shadows the
 * old one. That is how saving a lab note came back as an unacknowledged ending:
 * not a field wiped, a duplicate row standing in front of the real one.
 *
 * So a partial write carries one extra thing: the id of the row it means.
 */
export function outcomeWriteFor(
  recorded: Pick<CycleOutcome, "id"> | null | undefined,
  changes: CycleOutcomePatch,
): CycleOutcomePatch {
  return {
    ...(recorded?.id ? { id: recorded.id } : {}),
    ...changes,
  };
}

/**
 * Whether a note edit is worth a write at all.
 *
 * Two ways to get this wrong, both seen. Writing unconditionally sent
 * `{ notes: null }` on every step save, with no id — so it stored nothing and
 * inserted the shadowing row described above, and Home announced an ending on a
 * cycle that had not ended. Writing only for a non-empty note then made
 * *clearing* a note impossible: the text was gone from the form and still on
 * the record.
 *
 * So: write when the note actually changed, and never write to a cycle with no
 * outcome row unless there is something to store. A first note does create that
 * row, which is correct — the note has to live somewhere, and a row on an open
 * cycle announces nothing: Home's closing card needs a cycle that is closed.
 */
export function outcomeNoteNeedsWriting(args: {
  recorded: Pick<CycleOutcome, "notes"> | null | undefined;
  note: string | null;
}): boolean {
  const { recorded, note } = args;
  const stored = recorded?.notes ?? null;
  if (note === stored) return false;
  return note !== null || recorded != null;
}
